"use client";

import Link from "@/lib/localized-link";
import { Crown } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/locale-context";

/**
 * The plan pill in the nav bar.
 *
 *   Anonymous       -> nothing (the free-analyses count left the bar on
 *                      2026-10-18; the analyze page says what a device gets)
 *   Free signed-in  -> nothing. An account has no analysis limit
 *                      (owner, 2026-10-18), so there is no number to show
 *                      and no balance to top up.
 *   Pro trial       -> crown + "Pro · N days" (the full wording in its title)
 *   Pro subscriber  -> crown + "Pro" (owner, 2026-09-19: "instead of the
 *                      balance, show Pro there, nicely")
 */
export default function TokenBadge() {
  const { user, tokens, loading } = useAuth();
  const t = useT();

  if (!tokens) return null;

  // Anonymous: nothing. The "N free analyses" pill that stood here went on
  // 2026-10-18 (owner): the analyze page says what a device gets.
  if (!user) return null;

  // ACTIVE TRIAL: the pill is the trial status (crown + days left).
  if (user.pro_preview_days_left != null && !tokens.is_subscriber) {
    return (
      <Link
        href="/pricing"
        className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-2.5 py-1 text-xs font-bold text-gray-50 shadow-sm transition-all"
        title={t("trial.chip").replace("{n}", String(user.pro_preview_days_left))}
      >
        <Crown className="w-3 h-3" />
        {/* Short in the bar (owner, 2026-10-27: "remove the trial word,
            otherwise it doesn't fit"); the title keeps the full wording. */}
        <span className="tabular-nums">
          {t("trial.chip.short").replace("{n}", String(user.pro_preview_days_left))}
        </span>
      </Link>
    );
  }

  // SUBSCRIBER: the plan name - a dark pill (the quiet UI's Pro badge).
  if (tokens.is_subscriber) {
    return (
      <Link
        href="/pricing"
        className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-2.5 py-1 text-xs font-extrabold tracking-wide text-gray-50 shadow-sm transition-colors hover:bg-gray-700"
        title={t("pricing.subtitle.subscriber")}
      >
        <Crown className="h-3 w-3" />
        Pro
      </Link>
    );
  }

  // Free account: no limit, nothing to count.
  return null;
}
